import { StandardEvents } from '@shopify/events';

/**
 * Cart feedback for the redesign: fly-to-bag, bag bump and the "added to your bag" toast.
 *
 * - fly(image): animates a copy of a product image into the header bag along a curved path.
 * - bump(): springs the header bag button.
 * - The toast listens to the standard CartLinesUpdateEvent for every add on the site (redesign
 *   sections and the theme's product form) and shows "<product> added to your bag · View bag".
 *   It stays out of the way when the cart drawer is set to open automatically.
 *
 * Copy comes from <template id="WcCartToastStrings"> rendered by snippets/header-actions.liquid.
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function bagTarget() {
  return (
    document.querySelector('.header-actions__cart-icon--pill') ??
    document.querySelector('header-component cart-icon') ??
    document.querySelector('[data-testid="cart-drawer-trigger"]')
  );
}

let pendingFlights = 0;
/** Resolves when the most recent flight has landed. */
let lastFlight = Promise.resolve();

/**
 * @param {HTMLImageElement | null | undefined} image
 * @returns {Promise<void>}
 */
export function fly(image) {
  const target = bagTarget();
  if (!image || !target || reduceMotion() || !image.getBoundingClientRect().width) return Promise.resolve();

  const from = image.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  const size = Math.min(from.width, from.height, 220);
  const startX = from.left + from.width / 2 - size / 2;
  const startY = from.top + from.height / 2 - size / 2;
  const endX = to.left + to.width / 2 - size / 2;
  const endY = to.top + to.height / 2 - size / 2;
  const lift = Math.min(160, Math.abs(endY - startY) * 0.35 + 60);
  const peakY = Math.max(endY - 50, Math.min(startY, endY) - lift);

  const clone = document.createElement('img');
  clone.src = image.currentSrc || image.src;
  clone.alt = '';
  clone.setAttribute('aria-hidden', 'true');
  clone.className = 'wc-fly';
  clone.style.width = `${size}px`;
  clone.style.height = `${size}px`;
  document.body.append(clone);

  pendingFlights += 1;
  const animation = clone.animate(
    [
      { transform: `translate(${startX}px, ${startY}px) scale(1) rotate(0deg)`, opacity: 1, offset: 0 },
      {
        transform: `translate(${startX + (endX - startX) * 0.45}px, ${peakY}px) scale(0.7) rotate(-14deg)`,
        opacity: 1,
        offset: 0.4,
      },
      { transform: `translate(${endX}px, ${endY}px) scale(0.12) rotate(8deg)`, opacity: 0.9, offset: 1 },
    ],
    { duration: 850, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'forwards' }
  );

  lastFlight = animation.finished
    .catch(() => {})
    .then(() => {
      clone.remove();
      pendingFlights -= 1;
      bump();
    });
  return lastFlight;
}

export function bump() {
  const target = bagTarget();
  if (!(target instanceof HTMLElement) || reduceMotion()) return;
  target.animate(
    [{ scale: 1 }, { scale: 1.18 }, { scale: 0.94 }, { scale: 1.04 }, { scale: 1 }],
    { duration: 550, easing: 'ease-out' }
  );
}

/* ---------------------------------------------------------------- toast */

const strings = (() => {
  const template = document.getElementById('WcCartToastStrings');
  const data = template instanceof HTMLTemplateElement ? template.dataset : {};
  return {
    added: data.added || '[title] added to your bag',
    view: data.view || 'View bag',
    dismiss: data.dismiss || 'Dismiss',
  };
})();

/** @type {HTMLElement | undefined} */
let region;
/** @type {number | undefined} */
let hideTimer;

function ensureRegion() {
  if (region) return region;
  region = document.createElement('div');
  region.className = 'wc-toast-region';
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  document.body.append(region);
  return region;
}

function hide(toast) {
  if (!toast.isConnected) return;
  if (reduceMotion()) return toast.remove();
  toast
    .animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(20px) scale(0.95)' }], {
      duration: 200,
      easing: 'ease-in',
      fill: 'forwards',
    })
    .finished.then(() => toast.remove(), () => toast.remove());
}

/** @param {string} title */
export function toast(title) {
  const container = ensureRegion();
  container.querySelectorAll('.wc-toast').forEach((node) => node.remove());
  window.clearTimeout(hideTimer);

  const node = document.createElement('div');
  node.className = 'wc-toast';

  const check = document.createElement('span');
  check.className = 'wc-toast__check';
  check.setAttribute('aria-hidden', 'true');
  check.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

  const message = document.createElement('span');
  message.className = 'wc-toast__message';
  message.textContent = strings.added.replace('[title]', title);

  const view = document.createElement('button');
  view.type = 'button';
  view.className = 'wc-toast__action';
  view.textContent = strings.view;
  view.addEventListener('click', () => {
    hide(node);
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else window.location.href = Theme.routes.cart_url;
  });

  node.append(check, message, view);
  container.append(node);

  if (!reduceMotion()) {
    node.animate(
      [
        { opacity: 0, transform: 'translateY(40px) scale(0.9)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 520, easing: 'cubic-bezier(0.34, 1.4, 0.64, 1)' }
    );
  }

  // Auto-dismiss, but never while the shopper is hovering or focused on it.
  const schedule = () => {
    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(() => hide(node), 4000);
  };
  node.addEventListener('pointerenter', () => window.clearTimeout(hideTimer));
  node.addEventListener('pointerleave', schedule);
  node.addEventListener('focusin', () => window.clearTimeout(hideTimer));
  node.addEventListener('focusout', schedule);
  node.addEventListener('keydown', (event) => event.key === 'Escape' && hide(node));
  schedule();
}

/** @param {any} event */
async function handleCartLinesUpdate(event) {
  if (event.action !== 'add' || !event.promise) return;
  const drawerAutoOpens = document.querySelector('cart-drawer-component[auto-open]');

  let result;
  try {
    result = await event.promise;
  } catch {
    return;
  }
  if (!result || result.detail?.didError) return;

  if (pendingFlights) await lastFlight;
  else bump();
  if (drawerAutoOpens) return;

  const id = String(event.lines?.[0]?.merchandiseId ?? '').split('/').pop();
  const items = result.detail?.items ?? [];
  const item = items.find((line) => String(line.variant_id) === id || String(line.id) === id) ?? items[0];
  if (item) toast(item.product_title ?? item.title ?? '');
}

if (!window.__wcCartFx) {
  window.__wcCartFx = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, handleCartLinesUpdate);

  // The desktop scrim is a ::before on the cart drawer's section wrapper; clicking it closes the bag.
  document.addEventListener('click', (event) => {
    const section = document.getElementById('shopify-section-cart-drawer-section');
    if (event.target !== section) return;
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (drawer?.hasAttribute('open') && typeof drawer.close === 'function') drawer.close();
  });
}
